import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineBell, HiOutlineSparkles, HiOutlineChat, HiOutlineEye,
  HiHeart, HiOutlineCurrencyDollar, HiOutlinePhone,
} from 'react-icons/hi';
import { creatorAPI, videoCallAPI } from '../services/api';
import LoadingAnimation from '../components/LoadingAnimation';
import './Notifications.css';

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [missedCalls, setMissedCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // all, creator, calls
  const [clearedCallIds, setClearedCallIds] = useState(() => {
    // Load cleared call IDs from localStorage
    const saved = localStorage.getItem('clearedMissedCalls');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      
      // Load creator notifications
      try {
        const notifRes = await creatorAPI.getNotifications();
        setNotifications(notifRes.data || []);
      } catch (err) {
        console.log('No creator notifications');
      }
      
      // Load missed calls and filter out cleared ones
      try {
        const callsRes = await videoCallAPI.getMissedCalls(0, 50);
        const allCalls = callsRes.data.content || [];
        // Filter out calls that have been cleared
        const unclearedCalls = allCalls.filter(call => !clearedCallIds.includes(call.id));
        setMissedCalls(unclearedCalls);
      } catch (err) {
        console.log('No missed calls');
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await creatorAPI.markAsRead(notification.id);
      setNotifications(notifications.filter(n => n.id !== notification.id));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
    
    // Navigate based on type
    if (notification.notificationType === 'NEW_COMMENT' && notification.relatedContentId) {
      // For comments, go to creator profile and scroll to the specific post
      navigate(`/creator-dashboard?highlight=${notification.relatedContentId}`);
    } else if (notification.notificationType === 'CONTENT_LIKE' && notification.relatedContentId) {
      // For likes, go to creator profile and scroll to the specific post
      navigate(`/creator-dashboard?highlight=${notification.relatedContentId}`);
    } else if (notification.notificationType === 'CONTENT_VIEW' && notification.relatedContentId) {
      // For views, go to creator profile and scroll to the specific post
      navigate(`/creator-dashboard?highlight=${notification.relatedContentId}`);
    } else if (notification.relatedUserId) {
      // For other notifications (subscribers, tips), go to user profile
      navigate(`/profile/${notification.relatedUserId}`);
    }
  };

  const handleMissedCallClick = async (call) => {
    // Mark as cleared
    const newClearedIds = [...clearedCallIds, call.id];
    setClearedCallIds(newClearedIds);
    localStorage.setItem('clearedMissedCalls', JSON.stringify(newClearedIds));
    
    // Remove from list
    setMissedCalls(missedCalls.filter(c => c.id !== call.id));
    
    // Navigate to caller's profile
    navigate(`/profile/${call.callerId}`);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await creatorAPI.markAllAsRead();
      setNotifications([]);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleClearAllMissedCalls = () => {
    // Mark all current missed calls as cleared
    const allCallIds = missedCalls.map(c => c.id);
    const newClearedIds = [...clearedCallIds, ...allCallIds];
    setClearedCallIds(newClearedIds);
    localStorage.setItem('clearedMissedCalls', JSON.stringify(newClearedIds));
    
    setMissedCalls([]);
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  const allNotifications = [
    ...notifications.map(n => ({ ...n, type: 'creator' })),
    ...missedCalls.map(c => ({ ...c, type: 'call' }))
  ].sort((a, b) => new Date(b.createdAt || b.callTime) - new Date(a.createdAt || b.callTime));

  const filteredNotifications = activeTab === 'all' ? allNotifications :
    activeTab === 'creator' ? allNotifications.filter(n => n.type === 'creator') :
    allNotifications.filter(n => n.type === 'call');

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1>Notifications</h1>
        {(notifications.length > 0 || missedCalls.length > 0) && (
          <button onClick={() => {
            handleMarkAllAsRead();
            handleClearAllMissedCalls();
          }} className="btn-clear-all">
            Clear All
          </button>
        )}
      </div>

      <div className="notifications-tabs">
        <button 
          className={activeTab === 'all' ? 'active' : ''}
          onClick={() => setActiveTab('all')}
        >
          All ({allNotifications.length})
        </button>
        <button 
          className={activeTab === 'creator' ? 'active' : ''}
          onClick={() => setActiveTab('creator')}
        >
          Creator ({notifications.length})
        </button>
        <button 
          className={activeTab === 'calls' ? 'active' : ''}
          onClick={() => setActiveTab('calls')}
        >
          Missed Calls ({missedCalls.length})
        </button>
      </div>

      <div className="notifications-list">
        {filteredNotifications.length === 0 ? (
          <div className="empty-notifications">
            <div className="empty-icon" aria-hidden="true"><HiOutlineBell /></div>
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          filteredNotifications.map((item) => (
            item.type === 'creator' ? (
              <div 
                key={`notif-${item.id}`}
                className="notification-item"
                onClick={() => handleNotificationClick(item)}
              >
                <div className="notification-icon creator" aria-hidden="true">
                  {item.notificationType === 'NEW_SUBSCRIBER' && <HiOutlineSparkles />}
                  {item.notificationType === 'NEW_COMMENT' && <HiOutlineChat />}
                  {item.notificationType === 'CONTENT_VIEW' && <HiOutlineEye />}
                  {item.notificationType === 'CONTENT_LIKE' && <HiHeart />}
                  {item.notificationType === 'TIP_RECEIVED' && <HiOutlineCurrencyDollar />}
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h4>{item.title}</h4>
                    <span className="notification-time">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p>{item.message}</p>
                  {item.relatedUserPhoto && (
                    <div className="notification-user">
                      <img src={item.relatedUserPhoto} alt={item.relatedUserName} />
                      <span>{item.relatedUserName}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div 
                key={`call-${item.id}`}
                className="notification-item missed-call"
                onClick={() => handleMissedCallClick(item)}
              >
                <div className="notification-icon call" aria-hidden="true">
                  <HiOutlinePhone />
                </div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h4>Missed Call</h4>
                    <span className="notification-time">
                      {new Date(item.callTime).toLocaleString()}
                    </span>
                  </div>
                  <p>Missed a call from {item.callerName}</p>
                  {item.callerPhotoUrl && (
                    <div className="notification-user">
                      <img src={item.callerPhotoUrl} alt={item.callerName} />
                      <span>{item.callerName}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;
